export const state = {
    products: [],
    cartItems: [], // Array of { product: {...}, quantity: number }
    checkoutState: 'browsing', // 'browsing' | 'cart' | 'checkout' | 'payment' | 'confirmed'
    loading: false,
    submitting: false,
    error: null,
    memberOrders: [],
    memberProfile: null,
    ordersLoading: false,
    profileLoading: false,
    selectedOrder: null,
};

export const getters = {
    cartTotal(state) {
        return state.cartItems.reduce((total, item) => {
            return total + (parseFloat(item.product.price) || 0) * item.quantity;
        }, 0);
    },
    cartItemCount(state) {
        return state.cartItems.reduce((count, item) => count + item.quantity, 0);
    },
    isSubmitting(state) {
        return state.submitting;
    },
    memberOrderCount(state) {
        return state.memberOrders.length;
    },
    hasOrders(state) {
        return state.memberOrders.length > 0;
    },
};

export const actions = {
    fetchProducts({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        return new Promise((resolve, reject) => {
            window.axios
                .get('api/products/member_products')
                .then((response) => {
                    commit('SET_PRODUCTS', response.data);
                    commit('SET_LOADING', false);
                    resolve(response.data);
                })
                .catch((error) => {
                    commit('SET_ERROR', error.response?.data || { error: 'Failed to load products' });
                    commit('SET_LOADING', false);
                    reject(error);
                });
        });
    },

    addToCart({ commit, state }, { product, quantity = 1 }) {
        const existing = state.cartItems.find(item => item.product.id === product.id);
        if (existing) {
            commit('UPDATE_CART_QUANTITY', {
                productId: product.id,
                quantity: existing.quantity + quantity,
            });
        } else {
            commit('ADD_TO_CART', { product, quantity });
        }
    },

    updateCartQuantity({ commit }, { productId, quantity }) {
        if (quantity < 1) return;
        commit('UPDATE_CART_QUANTITY', { productId, quantity });
    },

    removeFromCart({ commit }, productId) {
        commit('REMOVE_FROM_CART', productId);
    },

    async submitCheckout({ commit, state }) {
        commit('SET_SUBMITTING', true);
        commit('SET_ERROR', null);
        try {
            const items = state.cartItems.map(item => ({
                product_id: item.product.id,
                quantity: item.quantity,
            }));
            const response = await window.axios.post('api/orders/member_checkout', { items });
            commit('SET_SUBMITTING', false);
            return response.data;
        } catch (error) {
            commit('SET_SUBMITTING', false);
            commit('SET_ERROR', error.response ? error.response.data : { error: 'Network error' });
            throw error;
        }
    },

    async fetchMemberOrders({ commit }) {
        commit('SET_ORDERS_LOADING', true);
        try {
            const response = await window.axios.get('api/orders/member_orders');
            commit('SET_MEMBER_ORDERS', response.data.orders || []);
        } catch (error) {
            commit('SET_ERROR', error.response ? error.response.data : { error: 'Failed to load orders' });
        } finally {
            commit('SET_ORDERS_LOADING', false);
        }
    },

    async fetchMemberProfile({ commit }) {
        commit('SET_PROFILE_LOADING', true);
        try {
            const response = await window.axios.get('api/clients/member_profile');
            commit('SET_MEMBER_PROFILE', response.data.profile || null);
        } catch (error) {
            commit('SET_ERROR', error.response ? error.response.data : { error: 'Failed to load profile' });
        } finally {
            commit('SET_PROFILE_LOADING', false);
        }
    },

    selectOrder({ commit }, orderId) {
        commit('SET_SELECTED_ORDER', orderId);
    },
};

export const mutations = {
    SET_PRODUCTS(state, products) {
        state.products = products;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_SUBMITTING(state, submitting) {
        state.submitting = submitting;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_CHECKOUT_STATE(state, checkoutState) {
        state.checkoutState = checkoutState;
    },
    ADD_TO_CART(state, { product, quantity }) {
        state.cartItems.push({ product, quantity });
    },
    UPDATE_CART_QUANTITY(state, { productId, quantity }) {
        const item = state.cartItems.find(i => i.product.id === productId);
        if (item) {
            item.quantity = quantity;
        }
    },
    REMOVE_FROM_CART(state, productId) {
        state.cartItems = state.cartItems.filter(i => i.product.id !== productId);
    },
    CLEAR_CART(state) {
        state.cartItems = [];
        state.checkoutState = 'browsing';
    },
    SET_MEMBER_ORDERS(state, orders) {
        state.memberOrders = orders;
    },
    SET_MEMBER_PROFILE(state, profile) {
        state.memberProfile = profile;
    },
    SET_ORDERS_LOADING(state, loading) {
        state.ordersLoading = loading;
    },
    SET_PROFILE_LOADING(state, loading) {
        state.profileLoading = loading;
    },
    SET_SELECTED_ORDER(state, orderId) {
        state.selectedOrder = orderId;
    },
};
